import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, useI18n } from "@/i18n";
import { usePageMeta, eur, initials } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ChevronLeft, Clock, Diamond, CreditCard, ShieldCheck, Plus, Check } from "lucide-react";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function creditPriceOf(o) {
  if (!o) return 0;
  return Number(o.price_credits || 0) || Math.ceil(Number(o.price_cents || 0) / 10);
}

export default function SessionBooking() {
  const [sp] = useSearchParams();
  const coachId = sp.get("coach_id") || "";
  const offeringIdParam = sp.get("offering_id") || "";
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const t = useT();
  const { locale } = useI18n();

  const [loading, setLoading] = useState(true);
  const [coach, setCoach] = useState(null);
  const [offerings, setOfferings] = useState([]);
  const [offeringId, setOfferingId] = useState(offeringIdParam);
  const [resolvedDays, setResolvedDays] = useState([]);
  const [busy, setBusy] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [slot, setSlot] = useState("");
  const [mode, setMode] = useState("credits");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);

  const load = useCallback(async () => {
    if (!coachId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [c, offs, avail] = await Promise.all([
        api.get(`/users/lookup/${coachId}`).then((r) => r.data).catch(() => null),
        api.get(`/coach/offerings?coach_id=${coachId}`).then((r) => r.data).catch(() => []),
        api.get(`/coach/${coachId}/availability/v2/resolved`).then((r) => r.data).catch(() => null),
      ]);
      setCoach(c);
      const list = Array.isArray(offs) ? offs : (offs?.offerings || []);
      setOfferings(list);
      if (!offeringIdParam && list.length === 1) setOfferingId(list[0].id);
      setResolvedDays(Array.isArray(avail?.resolved) ? avail.resolved : []);
    } finally { setLoading(false); }
  }, [coachId, offeringIdParam]);
  useEffect(() => { load(); }, [load]);

  const offering = useMemo(() => offerings.find((o) => o.id === offeringId) || null, [offerings, offeringId]);
  const creditPrice = creditPriceOf(offering);
  const userCredits = Number(user?.credits || 0);
  const insufficient = mode === "credits" && userCredits < creditPrice;

  useEffect(() => {
    if (!offering) return;
    setMode(userCredits >= creditPrice ? "credits" : "stripe");
  }, [offeringId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!coachId || !date) { setBusy([]); return; }
    let on = true;
    api.get(`/coach/${coachId}/busy-slots?date=${date}`)
      .then((r) => { if (on) setBusy(Array.isArray(r.data?.busy) ? r.data.busy : []); })
      .catch(() => { if (on) setBusy([]); });
    return () => { on = false; };
  }, [coachId, date]);

  const availableSlots = useMemo(() => {
    if (!offering || !resolvedDays.length) return [];
    const duration = Number(offering.duration_min) || 60;
    const dayDoc = resolvedDays.find((d) => d.date === date);
    if (!dayDoc || !dayDoc.available || dayDoc.is_past || !(dayDoc.slots || []).length) return [];
    const step = 30; const nowMs = Date.now(); const out = [];
    for (const w of dayDoc.slots) {
      const [sh, sm] = String(w.start).split(":").map((x) => parseInt(x, 10));
      const [eh, em] = String(w.end).split(":").map((x) => parseInt(x, 10));
      if ([sh, sm, eh, em].some((v) => Number.isNaN(v))) continue;
      const ws = new Date(`${date}T00:00:00`); ws.setHours(sh, sm, 0, 0);
      const we = new Date(`${date}T00:00:00`); we.setHours(eh, em, 0, 0);
      const latest = we.getTime() - duration * 60000;
      for (let tms = ws.getTime(); tms <= latest; tms += step * 60000) {
        if (tms < nowMs) continue;
        const cEnd = tms + duration * 60000;
        const conflict = (busy || []).some((b) => {
          const bs = new Date(b.start_at).getTime();
          const be = bs + Number(b.duration_min || 60) * 60000;
          return tms < be && bs < cEnd;
        });
        if (!conflict) out.push(new Date(tms).toISOString());
      }
    }
    return out;
  }, [date, offeringId, resolvedDays, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasAvailability = useMemo(
    () => resolvedDays.some((d) => d.available && (d.slots || []).length > 0),
    [resolvedDays],
  );

  const timeLabel = (iso) => { try { return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  const confirm = async () => {
    if (!offering || booking) return;
    setBooking(true);
    try {
      const body = { payment_mode: mode, origin_url: window.location.origin };
      if (slot) body.scheduled_at = slot;
      if (notes.trim()) body.notes = notes.trim();
      const { data } = await api.post(`/coach/offerings/${offering.id}/book`, body);
      if (data?.checkout_url) { window.location.assign(data.checkout_url); return; }
      toast.success(t("sb.booked"));
      await refreshUser();
      navigate("/app/bookings");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "insufficient_credits") { setMode("stripe"); toast.error(t("sb.credits_insufficient", { need: d.needed, have: d.have })); }
      else toast.error(apiError(e, t("sb.book_failed")));
    } finally { setBooking(false); }
  };

  if (loading) return <Spinner label={t("common.loading")} />;
  if (!coach) {
    return (
      <div className="space-y-4">
        <BackBtn t={t} navigate={navigate} />
        <p className="text-sm text-muted-foreground">{t("sb.not_found")}</p>
      </div>
    );
  }

  const photo = coach.photo || coach.profile?.photo;

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="session-booking-screen">
      <BackBtn t={t} navigate={navigate} />

      <div className="flex flex-col items-center gap-2 text-center">
        <Avatar className="h-20 w-20">
          {photo ? <AvatarImage src={fileUrl(photo)} alt={coach.name} /> : null}
          <AvatarFallback className="text-xl">{initials(coach.name)}</AvatarFallback>
        </Avatar>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("sb.title")}</p>
        <h1 className="font-display text-3xl text-foreground">{coach.name}</h1>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">{t("sb.pick_offering")}</label>
        {offerings.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">{t("sb.no_offerings")}</p>
        ) : (
          <div className="space-y-2" data-testid="sb-offering-list">
            {offerings.map((o) => {
              const active = o.id === offeringId;
              return (
                <button
                  key={o.id}
                  data-testid={`sb-offering-${o.id}`}
                  onClick={() => { setOfferingId(o.id); setSlot(""); }}
                  className={`flex w-full items-start justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-primary bg-accent/40" : "border-border bg-card hover:bg-accent/30"}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{o.title}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                      {o.type_label || String(o.type || "").replace(/_/g, " ")}
                      {o.duration_min ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t("sb.minutes", { n: o.duration_min })}</span> : null}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-medium text-foreground">{eur(o.price_cents, o.currency || "EUR")}</p>
                    <p className="text-xs text-primary">{creditPriceOf(o)} {t("sb.credits_label")}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {offering && (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t("sb.payment")}</label>
            <div className="flex gap-2">
              <button
                data-testid="sb-pay-credits"
                onClick={() => setMode("credits")}
                className={`relative flex-1 rounded-xl border p-4 text-left transition-colors ${mode === "credits" ? "border-primary bg-accent/40" : "border-border bg-card hover:bg-accent/30"}`}
              >
                <span className="flex items-center gap-2 text-sm font-medium text-foreground"><Diamond className="h-4 w-4" /> {t("sb.pay_credits")}</span>
                <span className="mt-1 block font-display text-lg text-foreground">{creditPrice} {t("sb.credits_label")}</span>
                <span className={`mt-0.5 block text-xs ${insufficient ? "text-rose-600" : "text-muted-foreground"}`}>
                  {insufficient ? t("sb.credits_insufficient", { need: Math.max(0, creditPrice - userCredits), have: userCredits }) : t("sb.credits_balance", { balance: userCredits })}
                </span>
                {mode === "credits" && !insufficient ? <Check className="absolute right-3 top-3 h-4 w-4 text-primary" /> : null}
              </button>
              <button
                data-testid="sb-pay-stripe"
                onClick={() => setMode("stripe")}
                className={`relative flex-1 rounded-xl border p-4 text-left transition-colors ${mode === "stripe" ? "border-primary bg-accent/40" : "border-border bg-card hover:bg-accent/30"}`}
              >
                <span className="flex items-center gap-2 text-sm font-medium text-foreground"><CreditCard className="h-4 w-4" /> {t("sb.pay_card")}</span>
                <span className="mt-1 block font-display text-lg text-foreground">{eur(offering.price_cents, offering.currency || "EUR")}</span>
                {mode === "stripe" ? <Check className="absolute right-3 top-3 h-4 w-4 text-primary" /> : null}
              </button>
            </div>
            {insufficient ? (
              <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => navigate("/app/credits")} data-testid="sb-add-credits">
                <Plus className="h-4 w-4" /> {t("sb.add_credits")}
              </Button>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t("sb.preferred_time")}</label>
            <Input type="date" value={date} min={todayISO()} onChange={(e) => { setDate(e.target.value); setSlot(""); }} className="w-full rounded-xl sm:w-56" data-testid="sb-date-input" />
            {hasAvailability ? (
              <div className="rounded-xl border border-border bg-card p-4" data-testid="sb-available-slots">
                <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                  {availableSlots.length > 0 ? t("sb.available_times") : t("sb.no_slots_day")}
                </p>
                {availableSlots.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {availableSlots.map((iso) => {
                      const active = slot === iso;
                      return (
                        <button key={iso} data-testid={`sb-slot-${iso}`} onClick={() => setSlot(iso)}
                          className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/40 text-foreground hover:bg-accent/40"}`}>
                          {timeLabel(iso)}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : (
              <Input type="time" onChange={(e) => { if (e.target.value) { const d = new Date(`${date}T${e.target.value}`); setSlot(d.toISOString()); } }} className="w-full rounded-xl sm:w-40" data-testid="sb-time-input" />
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t("sb.note_title")}</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("sb.note_ph")} className="min-h-[90px]" data-testid="sb-notes" />
          </div>

          <div className="flex items-start gap-2 rounded-2xl bg-accent/40 p-4">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-xs text-muted-foreground">{t("sb.legal")}</p>
          </div>

          <Button className="w-full rounded-full" size="lg" disabled={booking || insufficient} onClick={confirm} data-testid="sb-confirm">
            {booking ? t("common.saving") : (insufficient ? t("sb.confirm_insufficient") : mode === "credits" ? t("sb.confirm_credits", { credits: creditPrice }) : t("sb.confirm_card", { amount: eur(offering.price_cents, offering.currency || "EUR") }))}
          </Button>
        </>
      )}
    </div>
  );
}

function BackBtn({ t, navigate }) {
  return (
    <button onClick={() => navigate(-1)} data-testid="sb-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ChevronLeft className="h-4 w-4" /> {t("sb.back")}
    </button>
  );
}
